import * as React from 'react'
import * as Tooltip from 'react-tooltip'
import { observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { CreateProduct } from '../AddProduct/ProductStore'
import { API, Unit } from '@getgreenline/homi-shared'
import { CurrencyInput } from '../../../../components/CurrencyInput'
import { FormError } from '../../../../components/FormError'
import { LoadingAnimation } from '../../../../components/LoadingAnimation'
import { Dropdown, Button, Icon, Menu } from 'antd'
import { ClickParam } from 'antd/lib/menu'

interface Props {
  pricingGroupId: string | null
  currentCompanyStore?: CurrentCompanyStore
  onClose: () => void
}

interface State {
  isSaving: boolean
  errorMessage?: string
}

@inject('currentCompanyStore')
@observer
export class EditPricingGroupModal extends React.Component<Props, State> {
  company = this.props.currentCompanyStore!.company!

  @observable
  pricingGroup = new CreateProduct()

  constructor(props: Props) {
    super(props)
    this.state = {
      isSaving: false,
    }
    this.pricingGroup.setIsMasterProduct(true)
  }

  async componentDidMount() {
    if (this.props.pricingGroupId) {
      const pricingGroup = await API.getPricingGroupById(this.company.id, this.props.pricingGroupId)
      this.pricingGroup.populateFields(pricingGroup)
    }
  }

  getButtonClass(mode: boolean | 'bulk' | 'prepack' | undefined) {
    const product = this.pricingGroup
    const defaultButtonClass = 'btn btn-lg w-100 '
    return (
      defaultButtonClass +
      (product.inventoryMode === mode
        ? 'btn-success'
        : 'btn-light btn-outline-secondary background-white')
    )
  }

  getTextClass(mode: boolean | 'bulk' | 'prepack' | undefined) {
    const product = this.pricingGroup
    return product.inventoryMode === mode ? 'text-black' : 'text-muted'
  }

  save() {
    this.setState({ isSaving: true, errorMessage: undefined })
    if (this.props.pricingGroupId) {
      // Editing existing
      API.updatePricingGroup(
        this.company.id,
        this.props.pricingGroupId,
        this.pricingGroup.networkObject,
      )
        .then(() => this.props.onClose())
        .catch((error) => {
          this.setState({ isSaving: false })
          this.setState({ errorMessage: error.response.data.message })
        })
    } else {
      // Creating
      API.createPricingGroup(this.company.id, this.pricingGroup.networkObject)
        .then(() => this.props.onClose())
        .catch((error) => {
          this.setState({ isSaving: false })
          this.setState({ errorMessage: error.response.data.message })
        })
    }
  }

  removeChildPricing = (index: number) => {
    if (
      window.confirm(
        'If there are existing variants attached to this pricing group, that variant will be deleted after saving. We recommend editing existing variant pricing groups first.',
      )
    ) {
      this.pricingGroup.removeChildProduct(index)
    }
  }

  onDropdownMenuClick = (param: ClickParam) => {
    const { key, domEvent } = param
    domEvent.stopPropagation()
    this.pricingGroup.setUnit(key as Unit)
  }

  get dropdownMenus() {
    return (
      <Menu onClick={this.onDropdownMenuClick}>
        <Menu.Item key='unit'>units</Menu.Item>
        {this.company.allowBulk && <Menu.Item key='gram'>grams</Menu.Item>}
      </Menu>
    )
  }

  render() {
    let body: JSX.Element | undefined
    if (this.props.pricingGroupId && !this.pricingGroup.id) {
      body = <LoadingAnimation />
    } else {
      body = (
        <div>
          <div className='form-group'>
            <label>Name</label>
            <input
              className='form-control'
              value={this.pricingGroup.name}
              onChange={(e) => this.pricingGroup.setName(e.target.value)}
            />
          </div>
          <br />
          <div className='form-group'>
            <label>Unit of measurement</label>

            <div>
              <Dropdown overlay={this.dropdownMenus}>
                <Button>
                  <span>{`${this.pricingGroup.unit}s`}</span>
                  <Icon type='down' />
                </Button>
              </Dropdown>
            </div>
          </div>
          <br />
          <div className='form-group'>
            <label>Pricing/weights</label>
            {!this.pricingGroup.isMasterProduct ? (
              <div className='row'>
                <div className='col-lg-5'>
                  <label>Retail price</label>
                  <CurrencyInput
                    className='form-control'
                    onlyPositiveNumber
                    centValue={this.pricingGroup.price}
                    onChange={(value) => this.pricingGroup.setPrice(value)}
                  />
                </div>
                {this.pricingGroup.unit === 'gram' && (
                  <div className='col-lg-5'>
                    <label>Units to remove from inventory</label>
                    <input
                      className='form-control text-right'
                      type='number'
                      value={this.pricingGroup.inventorySubtractAmount}
                      onChange={(e) => {
                        this.pricingGroup.setInventorySubtractAmount(
                          e.target.value ? parseFloat(e.target.value) : 0,
                        )
                      }}
                    />
                  </div>
                )}
                {this.pricingGroup.unit === 'unit' && (
                  <div className='col-lg-5'>
                    <label>
                      Net product weight{' '}
                      <span className='text-muted' style={{ fontSize: 12 }}>
                        (g, optional)
                      </span>
                    </label>
                    <input
                      className='form-control text-right'
                      type='number'
                      value={this.pricingGroup.weight || 0}
                      onChange={(e) => {
                        this.pricingGroup.setWeight(e.target.value ? parseFloat(e.target.value) : 0)
                      }}
                    />
                  </div>
                )}
                <div className='col-lg-5 mt-2'>
                  <label>
                    Cannabis weight{' '}
                    <span className='text-muted' style={{ fontSize: 12 }}>
                      (g, optional)
                    </span>
                  </label>
                  <input
                    className='form-control text-right'
                    type='number'
                    value={this.pricingGroup.cannabisWeight || 0}
                    onChange={(e) => {
                      this.pricingGroup.setCannabisWeight(
                        e.target.value ? parseFloat(e.target.value) : 0,
                      )
                    }}
                  />
                </div>
                <div className='col-lg-5 mt-2'>
                  <label>
                    Cannabis Volume{' '}
                    <span className='text-muted' style={{ fontSize: 12 }}>
                      (optional)
                    </span>
                  </label>
                  <input
                    className='form-control text-right'
                    type='number'
                    value={this.pricingGroup.cannabisVolume || 0}
                    onChange={(e) => {
                      this.pricingGroup.setCannabisVolume(
                        e.target.value ? parseFloat(e.target.value) : 0,
                      )
                    }}
                  />
                </div>
              </div>
            ) : (
              <div>
                {this.pricingGroup.childProducts.map((childProduct, index) => {
                  return (
                    <div className='row mb-2 pl-4 pr-4' key={index}>
                      <div className='col-lg-12'>
                        <div className='row p-3 border border-secondary'>
                          <div
                            style={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              cursor: 'pointer',
                              zIndex: 2,
                            }}
                            onClick={() => {
                              this.removeChildPricing(index)
                            }}
                          >
                            {!childProduct.id && (
                              <button className='btn btn-sm btn-danger'>
                                <span className='fa fa-trash' />
                              </button>
                            )}
                          </div>

                          <div className='col-lg-6'>
                            <label>Name</label>
                            <input
                              className='form-control'
                              value={childProduct.name}
                              onChange={(e) => childProduct.setName(e.target.value)}
                            />
                          </div>
                          <div className='col-lg-4'>
                            <label>Retail price</label>
                            <CurrencyInput
                              className='form-control'
                              onlyPositiveNumber
                              centValue={childProduct.price}
                              onChange={(value) => childProduct.setPrice(value)}
                            />
                          </div>

                          {childProduct.unit === 'gram' && (
                            <div className='col-lg-3 mt-2'>
                              <label>Grams to subtract</label>
                              <input
                                className='form-control text-right'
                                type='number'
                                value={childProduct.inventorySubtractAmount.toString()}
                                onChange={(e) => {
                                  childProduct.setInventorySubtractAmount(
                                    e.target.value ? parseFloat(e.target.value) : 0,
                                  )
                                }}
                              />
                            </div>
                          )}
                          {this.pricingGroup.unit === 'unit' && (
                            <div className='col-lg-3 mt-2'>
                              <label>
                                Shiping weight{' '}
                                <span className='text-muted' style={{ fontSize: 12 }}>
                                  (g, optional)
                                </span>
                              </label>
                              <input
                                className='form-control text-right'
                                type='number'
                                value={childProduct.weight || 0}
                                onChange={(e) => {
                                  childProduct.setWeight(
                                    e.target.value ? parseFloat(e.target.value) : 0,
                                  )
                                }}
                              />
                            </div>
                          )}

                          <div className='col-lg-4 mt-2'>
                            <label>
                              Cannabis weight{' '}
                              <span className='text-muted' style={{ fontSize: 12 }}>
                                (g, optional)
                              </span>
                            </label>
                            <input
                              className='form-control text-right'
                              type='number'
                              value={childProduct.cannabisWeight || 0}
                              onChange={(e) => {
                                childProduct.setCannabisWeight(
                                  e.target.value ? parseFloat(e.target.value) : 0,
                                )
                              }}
                            />
                          </div>
                          <div className='col-lg-4 mt-2'>
                            <label>
                              Cannabis Volume{' '}
                              <span className='text-muted' style={{ fontSize: 12 }}>
                                (ml, optional)
                              </span>
                            </label>
                            <input
                              className='form-control text-right'
                              type='number'
                              value={childProduct.cannabisVolume || 0}
                              onChange={(e) => {
                                childProduct.setCannabisVolume(
                                  e.target.value ? parseFloat(e.target.value) : 0,
                                )
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
                <br />
                <div>
                  <button
                    className='btn btn-outline-success'
                    onClick={() => this.pricingGroup.addChildProduct()}
                  >
                    Add variant
                  </button>
                </div>
              </div>
            )}
          </div>
          <br />
          <div className='form-group'>
            <label>Inventory mode</label>
            <div className='row'>
              {!this.pricingGroup.isMasterProduct && (
                <div className='col-lg-4'>
                  <div
                    className={
                      'rounded cursor-pointer p-4 ' +
                      (this.pricingGroup.inventoryMode === true
                        ? `bg-success text-white`
                        : `bg-light`)
                    }
                    onClick={() => {
                      this.pricingGroup.setInventoryMode(true)
                    }}
                  >
                    <span className='mr-2'>Track inventory</span>
                    <span
                      className='fa fa-info-circle text-muted mr-4'
                      data-tip
                      data-for='track-inventory'
                    />
                    <Tooltip id='track-inventory' place='right' effect='solid'>
                      <div>On by default.</div>
                      <div>
                        Recommended. This product will show up on the 'Inventory' tab and have
                        inventory for each location.
                      </div>
                    </Tooltip>
                  </div>
                </div>
              )}

              {this.pricingGroup.isMasterProduct && (
                <>
                  {this.company.allowBulk && (
                    <div className='col-lg-4'>
                      <div
                        className={
                          'rounded cursor-pointer p-4 ' +
                          (this.pricingGroup.inventoryMode === 'bulk'
                            ? `bg-success text-white`
                            : `bg-light`)
                        }
                        onClick={() => {
                          this.pricingGroup.setInventoryMode('bulk')
                        }}
                      >
                        <span className='mr-2'>Bulk product</span>
                        <span
                          className='fa fa-info-circle text-muted mr-4'
                          data-tip
                          data-for='bulk-product'
                        />
                        <Tooltip id='bulk-product' place='right' effect='solid'>
                          <div>For selling products out-of-jar.</div>
                          <div>Variants all share inventory from the parent product.</div>
                        </Tooltip>
                      </div>
                    </div>
                  )}
                  <div className='col-lg-4'>
                    <div
                      className={
                        'rounded cursor-pointer p-4 ' +
                        (this.pricingGroup.inventoryMode === 'prepack'
                          ? `bg-success text-white`
                          : `bg-light`)
                      }
                      onClick={() => {
                        this.pricingGroup.setInventoryMode('prepack')
                      }}
                    >
                      <span className='mr-2'>Variant/pre-pack</span>
                      <span
                        className='fa fa-info-circle text-muted mr-4'
                        data-tip
                        data-for='pre-pack'
                      />
                      <Tooltip id='pre-pack' place='right' effect='solid'>
                        <div>Default</div>
                        <div>Each variant will hold their own inventory.</div>
                      </Tooltip>
                    </div>
                  </div>
                </>
              )}

              <div className='col-lg-4'>
                <div
                  className={
                    'rounded cursor-pointer p-4 ' +
                    (this.pricingGroup.inventoryMode === false
                      ? `bg-success text-white`
                      : `bg-light`)
                  }
                  onClick={() => {
                    this.pricingGroup.setInventoryMode(false)
                  }}
                >
                  <span className='mr-2'>Don't track</span>
                  <span
                    className='fa fa-info-circle text-muted mr-4'
                    data-tip
                    data-for='dont-track-inventory'
                  />
                  <Tooltip id='dont-track-inventory' place='right' effect='solid'>
                    <div>Useful for selling services rather than products.</div>
                    <div>We recommend tracking the inventory of all physical goods.</div>
                  </Tooltip>
                </div>
              </div>
            </div>

            {this.pricingGroup.inventoryMode === 'bulk' && this.pricingGroup.unit === 'gram' && (
              <>
                <br />
                <div className='text-muted'>Bulk pricing mode</div>
                <div className='row'>
                  <div className='col-lg-4'>
                    <div
                      className={
                        'rounded cursor-pointer p-4 ' +
                        (this.pricingGroup.inBetweenPricing ? `bg-light` : `bg-success text-white`)
                      }
                      onClick={() => {
                        this.pricingGroup.setInBetweenPricing(false)
                      }}
                    >
                      <span className='mr-2'>Use highest weight price (default)</span>
                      <span
                        className='fa fa-info-circle text-muted mr-4'
                        data-tip
                        data-for='in-between-pricing-yes'
                      />
                      <Tooltip id='in-between-pricing-yes' place='right' effect='solid'>
                        <div>Default</div>
                        <div>Example: Sell 8 grams</div>
                        <div>2.3 units of 3.5 grams</div>
                      </Tooltip>
                    </div>
                  </div>
                  <div className='col-lg-4'>
                    <div
                      className={
                        'rounded cursor-pointer p-4 ' +
                        (this.pricingGroup.inBetweenPricing ? `bg-success text-white` : `bg-light`)
                      }
                      onClick={() => {
                        this.pricingGroup.setInBetweenPricing(true)
                      }}
                    >
                      <span className='mr-2'>Use multiple weight prices</span>
                      <span
                        className='fa fa-info-circle text-muted mr-4'
                        data-tip
                        data-for='in-between-pricing-no'
                      />
                      <Tooltip id='in-between-pricing-no' place='right' effect='solid'>
                        <div>Example: Sell 8 grams</div>
                        <div>2 units of 3.5 grams + 1 unit of 1 gram</div>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Only Trees (ID 151) should have access to cumulative pricing */}
            {this.company.id === 151 &&
              this.pricingGroup.inventoryMode === 'prepack' &&
              this.pricingGroup.unit === 'unit' && (
                <>
                  <br />
                  <div className='mb-2'>
                    <span>Use cumulative pricing</span>
                  </div>
                  <div className='row'>
                    <div className='col-lg-4'>
                      <div
                        className={
                          'rounded cursor-pointer p-4 ' +
                          (this.pricingGroup.isCumulative ? `bg-success text-white` : `bg-light`)
                        }
                        onClick={() => {
                          this.pricingGroup.setIsCumulative(true)
                        }}
                      >
                        <span className='mr-2'>Yes</span>
                        <span
                          className='fa fa-info-circle text-muted mr-4'
                          data-tip
                          data-for='cumulative-yes'
                        />
                        <Tooltip id='cumulative-yes' place='right' effect='solid'>
                          <div>Product prices will be reduced as more grams are added to cart.</div>
                          <div>
                            The highest weight price-point variant will be used to calculate the
                            overall price.
                          </div>
                          <div>
                            Please make sure that your prices and weights for each variant are
                            defined correctly.
                          </div>
                          <div>
                            The price reductions will apply to all products with this pricing group
                            in the POS cart.
                          </div>
                          <div className='mt-2'>ex.</div>
                          <div>Buy 2 1/8oz pre-packs</div>
                          <div>Combined, they're priced as a single 1/4oz pre-pack</div>
                        </Tooltip>
                      </div>
                    </div>
                    <div className='col-lg-4'>
                      <div
                        className={
                          'rounded cursor-pointer p-4 ' +
                          (this.pricingGroup.isCumulative ? `bg-light` : `bg-success text-white`)
                        }
                        onClick={() => {
                          this.pricingGroup.setIsCumulative(false)
                        }}
                      >
                        <span className='mr-2'>No (default)</span>
                        <span
                          className='fa fa-info-circle text-muted mr-4'
                          data-tip
                          data-for='cumulative-no'
                        />
                        <Tooltip id='cumulative-no' place='right' effect='solid'>
                          <div>Default</div>
                          <div>Prices will remain the same when more products are added.</div>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </>
              )}

            <br />

            <div className='form-group'>
              {this.state.isSaving ? (
                <button className='btn btn-lg btn-success w-100' disabled>
                  Saving...
                </button>
              ) : (
                <button className='btn btn-lg btn-success w-100' onClick={() => this.save()}>
                  Save
                </button>
              )}
              <FormError message={this.state.errorMessage} />
            </div>
          </div>
        </div>
      )
    }

    return (
      <Modal isOpen={true} size='lg' toggle={() => this.props.onClose()}>
        <ModalHeader toggle={() => this.props.onClose()}>Pricing group</ModalHeader>
        <ModalBody>{body}</ModalBody>
      </Modal>
    )
  }
}
